/**
 * GDPR Component
 * STORY-LEGAL-001: GDPR/CCPA Compliance Information
 *
 * Displays data protection rights for EU (GDPR) and California (CCPA) users.
 * Provides information about:
 * - Data Subject Rights
 * - How to submit data requests
 * - Cookie consent information
 */
import React, { useEffect, useRef } from 'react';
import { X, Shield, FileText, Download, Trash2, Clock, Globe, CheckCircle } from 'lucide-react';
import { SUPPORT_CONFIG, getMailtoLink } from '../config/support.config';
import { useLockBodyScroll } from '../hooks/useLockBodyScroll';
// BRD-PRELAUNCH-ARTIFACTS-001 PHASE_A2 A-08: import shared content module
// (GDPR section lives in PrivacyContent). Phase A retains the existing rich
// modal body; Phase C migrates the rich content into PrivacyContent.jsx.
import PrivacyContent from './legal/PrivacyContent';

export default function GDPR({ onClose }) {
  // Prevent body scroll when modal is open
  useLockBodyScroll(true);

  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);

  // Keyboard handling for accessibility
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    firstFocusableRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gdpr-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Panel */}
      <div
        ref={modalRef}
        className="relative bg-zinc-900 rounded-2xl border border-zinc-800 w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800 flex-shrink-0">
          <h2 id="gdpr-title" className="text-xl font-bold text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-orange-500" />
            Data Protection Rights
          </h2>
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            aria-label="Close data protection information"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Introduction */}
          <section>
            <p className="text-zinc-300">
              Muñoz Digital Media, LLC respects your data protection rights under the General Data Protection
              Regulation (GDPR) for EU residents and the California Consumer Privacy Act (CCPA)
              for California residents.
            </p>
          </section>

          {/* GDPR Rights */}
          <section className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <h3 className="font-semibold text-blue-400 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Your GDPR Rights (EU Residents)
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <RightCard
                icon={<FileText className="w-4 h-4" />}
                title="Right to Access"
                description="Request a copy of all personal data we hold about you"
              />
              <RightCard
                icon={<CheckCircle className="w-4 h-4" />}
                title="Right to Rectification"
                description="Request correction of inaccurate or incomplete data"
              />
              <RightCard
                icon={<Trash2 className="w-4 h-4" />}
                title="Right to Erasure"
                description="Request deletion of your personal data ('right to be forgotten')"
              />
              <RightCard
                icon={<Download className="w-4 h-4" />}
                title="Right to Portability"
                description="Receive your data in a machine-readable format"
              />
            </div>
          </section>

          {/* CCPA Rights */}
          <section className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
            <h3 className="font-semibold text-green-400 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Your CCPA Rights (California Residents)
            </h3>
            <div className="text-sm text-zinc-400 space-y-2">
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-zinc-300">Right to Know:</strong> What personal information we collect and how it's used</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-zinc-300">Right to Delete:</strong> Request deletion of your personal information</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-zinc-300">Right to Opt-Out:</strong> Opt out of the sale of personal information (we do not sell personal data)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-zinc-300">Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your rights</span>
                </li>
              </ul>
            </div>
          </section>

          {/* How to Submit Requests */}
          <section className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-orange-500" />
              How to Submit a Data Request
            </h3>
            <div className="text-sm text-zinc-400 space-y-3">
              <p>To exercise any of your data protection rights:</p>
              <ol className="list-decimal list-inside ml-2 space-y-2">
                <li>
                  Email us at{' '}
                  <a href={getMailtoLink('privacy', 'Data Subject Access Request')} className="text-orange-500 hover:text-orange-400">
                    {SUPPORT_CONFIG.emails.privacy}
                  </a>
                </li>
                <li>Include "GDPR Request" or "CCPA Request" in the subject line</li>
                <li>Specify which right(s) you wish to exercise</li>
                <li>Provide enough information to verify your identity</li>
              </ol>
            </div>
          </section>

          {/* Response Timeline */}
          <section className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-orange-500" />
              Response Timeline
            </h3>
            <div className="text-sm text-zinc-400 space-y-2">
              <p>We will respond to your request within:</p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li><strong className="text-zinc-300">GDPR:</strong> 30 days (extendable by 60 days for complex requests)</li>
                <li><strong className="text-zinc-300">CCPA:</strong> 45 days (extendable by 45 days with notice)</li>
              </ul>
              <p className="mt-3 text-zinc-500">
                All requests are processed free of charge. We may require identity verification
                to protect your privacy.
              </p>
            </div>
          </section>

          {/* Data We Collect */}
          <section className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3">Categories of Data Collected</h3>
            <div className="text-sm text-zinc-400">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left py-2 text-zinc-300">Category</th>
                    <th className="text-left py-2 text-zinc-300">Examples</th>
                    <th className="text-left py-2 text-zinc-300">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  <tr>
                    <td className="py-2">Identifiers</td>
                    <td className="py-2">Name, email, address</td>
                    <td className="py-2">Order fulfillment</td>
                  </tr>
                  <tr>
                    <td className="py-2">Commercial</td>
                    <td className="py-2">Purchase history</td>
                    <td className="py-2">Customer service</td>
                  </tr>
                  <tr>
                    <td className="py-2">Internet Activity</td>
                    <td className="py-2">Browsing, search</td>
                    <td className="py-2">Site improvement</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Contact */}
          <section className="text-center pt-4 border-t border-zinc-800">
            <p className="text-sm text-zinc-400">
              For questions about data protection, contact our Privacy Team at{' '}
              <a href={getMailtoLink('privacy', 'Data Protection Question')} className="text-orange-500 hover:text-orange-400">
                {SUPPORT_CONFIG.emails.privacy}
              </a>
            </p>
            <p className="text-xs text-zinc-600 mt-2">
              Data Protection Officer: {SUPPORT_CONFIG.address.company}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

// Helper component for rights cards
function RightCard({ icon, title, description }) {
  return (
    <div className="bg-zinc-800/50 rounded-lg p-3">
      <div className="flex items-center gap-2 text-zinc-300 font-medium mb-1">
        {icon}
        {title}
      </div>
      <p className="text-xs text-zinc-500">{description}</p>
    </div>
  );
}
